// src/lib/footprint-editor/FootprintEditor.js
//
// A MapLibre-backed editor for the features on one plan revision (design
// §7.2): many features per instance, edited in the revision's UTM frame.
//
// ── IDS ARE THE INSTANCE'S ──────────────────────────────────────────────────
//
// Every source and layer id is `fe-<instanceId>-…`, so two editors share a map
// with each other, with PadPlannerMap's pp-* layers and with MlDraw's ml-draw-*
// ones (which are literals; see ml-draw.js's header). destroy() removes the
// ids this instance added and nothing else, and unbinds every handler it
// bound. The test mounts all four on one map.
//
// ── THE SERVER'S GEOMETRY WINS ──────────────────────────────────────────────
//
// What the editor draws for a feature is the server's geom (from the GET or
// the last save) while the feature is as the server saw it, and the client's
// preview (geometry.js) once it has been edited. Edits are compared by the
// fields 151's transform reads, so undoing back to the saved state shows the
// saved geometry again.
//
// ── THE FRAME ───────────────────────────────────────────────────────────────
//
// localEpsg is the revision's. When it is NULL (no feature saved yet) the
// editor previews in provisionalEpsg(perimeter), and applySaved() adopts the
// local_epsg the save returns before any further math (setFrame).
//
// Saving, tokens and 409s are the screen's: the editor gives it the payload
// (toPayload) and takes the response (applySaved).
//
// ── LINES ───────────────────────────────────────────────────────────────────
//
// Drawn with startLine (click per vertex, double-click or Enter to finish,
// Escape to cancel), and edited by their handles: drag a vertex ("v:<i>") to
// move it, drag or click a segment's midpoint ("m:<i>") to insert one, and
// Alt-click a vertex, or click it and press Delete, to remove it (a line
// keeps two). Each is one operation for undo. A vertex snaps, first that
// applies: another line's endpoint (lineEnds, so a road joins a road), a
// perimeter edge, the grid. A line is colored by its layer (lineColors) and
// labeled with its length; selected, each segment's length and the total.
//
// ── THE PAD AXIS ───────────────────────────────────────────────────────────
//
// setPadAxis({mode: "long"}) turns the grid snap to the pad's long side
// (padAxis.js: the minimum rotated rectangle in the frame, about the
// perimeter's centroid), {mode: "edge", ring, index} to one perimeter edge,
// null back to grid north. startEdgePick() makes the next click on a
// perimeter edge pick it (mode "edge"; Escape cancels). Rotation steps are
// then the pad's (15° from the axis); every rotation_deg stays absolute, and
// nothing about the axis is in a feature or a save: it is a view-and-snap
// aid, and the page keeps the choice (padplanner/padAlign.js).
//
// ── SYMBOLS (SY3) ───────────────────────────────────────────────────────────
//
// A footprint whose template has a symbol (166; symbols.js) gets the stencil
// master's drawing over its outline, in two layers of their own, "symbol-fill"
// and "symbol-line", from zoom 19 (Paper view: about 40 ft to the inch or
// finer, setDrawingStyle's paper). DISPLAY ONLY: neither layer is in
// _featureLayers, so a symbol takes no click or hover and selects nothing;
// snapping, align, the pad axis, measuring and the payload read features and
// footprints, never a symbol. Each feature's symbol is kept until its
// geometrySignature, its template or the frame changes, so a drag recomputes
// only what moves.
//
// ── READ-ONLY ───────────────────────────────────────────────────────────────
//
// readOnly: true is the same drawing with nothing that changes a feature
// (S6a2's plan page, a frozen revision, a viewer). A click selects the one
// feature under it, or clears; there are no handles, no drags, no box select,
// no keys, no place or line. Every operation that would change the features
// refuses read_only and changes nothing. What it draws is what an editable
// instance draws for the same features: the server's geom.
import History from "./history.js";
import { createFrame, provisionalEpsg, isUtmEpsg, perimeterCentroid } from "./frame.js";
import { padAxis, edgeNear } from "./padAxis.js";
import {
  LIMITS, normalizeDeg, previewGeometry, localRings, handlesFor, resizeFootprint, resizeZone,
  rotationToward, translate, snapMove, snapPointToGrid, perimeterLocal, align, dimensionLabels,
  metersPerPixel, scaleBarFeet, referenceEN, labelPoint, lineHandle, moveVertex, insertVertex, deleteVertex,
  snapLinePoint, lineLengthsFt, lineDimensionLabels, formatFeet, withoutRepeats, labelSegment,
} from "./geometry.js";
import { ftToM } from "../units.js";
import { cullLabels, LabelPool } from "../map-overlay/screenLabels.js";
import { symbolFeatures, symbolPaint, SYMBOL_MIN_ZOOM, paperSymbolMinZoom } from "./symbols.js";

const INSTANCE_RE = /^[a-z0-9]{1,24}$/;
const LAYER_RE = /^[a-z0-9][a-z0-9_]{0,39}$/;
const EMPTY = { type: "FeatureCollection", features: [] };
// Position snaps to the grid; drag-resize has its own step, so a 5 ft grid
// does not put an 8 ft width out of reach. Numeric entry snaps to neither.
const DEFAULT_SNAPPING = Object.freeze({
  grid: true, gridFt: 5, resize: true, resizeStepFt: 1, rotation: true, rotationStepDeg: 15, perimeter: true, perimeterPx: 8,
  lineEnds: true, lineEndPx: 10,
});
const LINE_COLOR = "#fbbf24";        // a line whose layer has no color
const DUPLICATE_OFFSET_FT = 10;
const ZONE_COLOR = "#f472b6";
const SELECT_COLOR = "#22d3ee";      // the selection, the draft, the box, a live handle
const zoneFirst = (zone, other) => ["case", ["==", ["get", "kind"], "zone"], zone, other];
/** The features' paint for a drawing style (setDrawingStyle). */
export function drawingPaint({ ink = null, outlines = false, zone = null } = {}) {
  return {
    fillColor: zoneFirst(zone ?? ZONE_COLOR, ink ?? "#e2e8f0"),
    fillOpacity: zoneFirst(outlines ? 0.05 : 0.12, outlines ? 0 : ink ? 0.18 : 0.3),
    lineColor: zoneFirst(zone ?? ZONE_COLOR, ink ?? "#f8fafc"),
  };
}
/** The selection's color and a line's when its layer has none, for a drawing style. */
const selectColor = (style) => style?.select ?? SELECT_COLOR;
const lineColorPaint = (style) => ["coalesce", ["get", "color"], style?.lineDefault ?? LINE_COLOR];
const handleColorPaint = (style) => {
  const c = selectColor(style);
  return ["case", ["==", ["get", "handle"], "rotate"], c, ["get", "hover"], c, ["get", "active"], c, ["==", ["get", "kind"], "mid"], "rgba(255,255,255,0.35)", "#ffffff"];
};
// Feature labels are DOM spans in the overlay, like the edge labels. They are
// kept smooth at 500 features by: one label point per feature computed in
// render() (not on every pan); spans pooled and reused, never rebuilt;
// points off screen skipped; one label per LABEL_CELL screen cell (selected
// features first), so a zoomed-out pad is not a pile of text; and at most
// MAX_LABELS drawn. The cull and the pool are lib/map-overlay/screenLabels.js,
// which the dashboard's plan pins use too.
export const MAX_LABELS = 200;
const LABEL_CELL = { w: 96, h: 18 };
const LABEL_MARGIN = 48;
// A press this close to a handle's center takes the handle, though MapLibre's
// hit (the drawn circle, 3.5 px for a midpoint) missed: the nearest one wins.
const HANDLE_SLOP_PX = 8;
// A line's labels stand this far off it, clear of a hovered handle (7 px and its stroke).
const LINE_LABEL_GAP = 10;
function makeFeatureLabel() {
  const s = document.createElement("span");
  s.className = "fe-label";
  Object.assign(s.style, {
    position: "absolute", left: "0", top: "0", font: "600 11px/1.2 ui-sans-serif, system-ui, sans-serif", color: "#f8fafc",
    background: "rgba(15, 23, 42, 0.8)", padding: "1px 5px", borderRadius: "3px", whiteSpace: "nowrap",
    maxWidth: "160px", overflow: "hidden", textOverflow: "ellipsis",
  });
  return s;
}

let seq = 0;

/** RFC 4122 v4; crypto.randomUUID needs a secure context, getRandomValues does not. */
export function uuid4() {
  if (typeof crypto?.randomUUID === "function") return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** The fields 151's transform reads: equal signatures, equal server geometry. */
export function geometrySignature(f) {
  return JSON.stringify([f.kind, f.template_id ?? null, f.anchor?.coordinates ?? null, f.rotation_deg ?? 0,
    f.length_ft ?? null, f.width_ft ?? null, f.radius_ft ?? null, f.line_geom?.coordinates ?? null]);
}

/** The PUT body's shape for one feature: what the server stores (lines as `frame` measures them), not what it derives. */
export function payloadFeature(f, frame = null) {
  return {
    id: f.id, kind: f.kind, template_id: f.template_id ?? null, anchor: f.anchor ?? null,
    rotation_deg: f.rotation_deg ?? 0, length_ft: f.length_ft ?? null, width_ft: f.width_ft ?? null,
    radius_ft: f.radius_ft ?? null, line_geom: withoutRepeats(f, frame).line_geom ?? null, well_api10: f.well_api10 ?? null,
    label: f.label ?? null, layer: f.layer, props: f.props ?? {},
  };
}

const refuse = (code, message) => ({ ok: false, code, message });
const READ_ONLY = Object.freeze({ ok: false, code: "read_only", message: "This layout is read-only." });
const isTextEntry = (t) => !!t && (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName ?? "") || t.isContentEditable === true);

export default class FootprintEditor {
  /**
   * @param {object} o
   * @param {object} o.map                 a loaded MapLibre map
   * @param {string} [o.instanceId]        [a-z0-9]{1,24}; one is generated when absent
   * @param {number|null} o.localEpsg      the revision's; NULL before its first feature save
   * @param {object|null} o.perimeter      the revision's perimeter (4326)
   * @param {object[]} [o.templates]       the templates the palette offers (footprint_ft, radius_ft, default_layer)
   * @param {object[]} [o.features]        GET's features, with the server's geom
   * @param {(e: object) => void} [o.onChange]
   * @param {(ids: string[]) => void} [o.onSelectionChange]
   * @param {(mode: string) => void} [o.onModeChange]  select | place | line | edge
   * @param {(choice: object|null) => void} [o.onPadAxisChange]  the pad-axis choice an edge pick made (THE PAD AXIS above)
   * @param {(moved: object[]|null) => void} [o.onPreview]  the dragged features as the drag would leave them, on each move; null when the drag ends
   * @param {boolean} [o.keyboard]         shortcuts while focus is inside the map's container (see _onKey)
   * @param {boolean} [o.overlay]          dimension labels and the scale bar (default true)
   * @param {false|{left?: number, right?: number, bottom?: number}} [o.scaleBar]  where the scale bar sits, in px; false for none
   * @param {boolean} [o.readOnly]         click selects one feature; nothing changes one (READ-ONLY above)
   * @param {Object<string,string>} [o.lineColors]  a line's color by its layer (LINES above)
   */
  constructor({ map, instanceId, localEpsg = null, perimeter = null, templates = [], features = [],
    onChange = null, onSelectionChange = null, onModeChange = null, onPadAxisChange = null, onPreview = null, keyboard = false, overlay = true,
    scaleBar = { left: 12, bottom: 12 }, readOnly = false, lineColors = null }) {
    if (!map) throw new Error("footprint-editor: a map is required");
    const iid = instanceId ?? `e${(++seq).toString(36)}`;
    if (!INSTANCE_RE.test(iid)) throw new Error(`footprint-editor: instanceId ${iid} is not [a-z0-9]{1,24}`);
    this.map = map;
    this.instanceId = iid;
    this.prefix = `fe-${iid}-`;
    this.onChange = onChange;
    this.onSelectionChange = onSelectionChange;
    this.onPadAxisChange = onPadAxisChange;
    this.padChoice = null;
    this._axisCache = null;
    this.onModeChange = onModeChange;
    this.onPreview = onPreview;
    this.scaleBarAt = scaleBar;
    this.readOnly = readOnly === true;
    this.history = new History();
    this.snapping = { ...DEFAULT_SNAPPING };
    this.drawingStyle = { ink: null, outlines: false, lineColors: lineColors ?? null, paper: false, select: null, zone: null, lineDefault: null };
    this._symbols = new Map();   // feature id -> {sig, template, frame, feats} (SYMBOLS above)
    this.templates = new Map();
    this.hiddenLayers = new Set();
    this.selection = new Set();
    this.features = [];
    this.serverGeom = new Map();
    this.mode = "select";        // select | place | line | edge
    this._placeSpec = null;
    this._draft = null;          // line in progress: lng/lat vertices
    this.vertex = null;          // {id, index}: the line vertex Delete removes
    this._drag = null;
    this._preview = null;        // id -> feature while dragging
    this._active = true;
    this._destroyed = false;
    this.setTemplates(templates);
    this.perimeter = perimeter;
    this.localEpsg = isUtmEpsg(localEpsg) ? localEpsg : null;
    this.frame = null;
    this._setFrameEpsg(this.localEpsg ?? provisionalEpsg(perimeter));
    this._attach({ keyboard: keyboard && !this.readOnly, overlay });
    this.load(features, { localEpsg });
  }

  // ── ids ───────────────────────────────────────────────────────────────────

  id(name) { return `${this.prefix}${name}`; }

  /** Every source and layer id this instance owns. */
  get ownedIds() {
    return {
      sources: ["features", "symbols", "handles", "box", "draft"].map((n) => this.id(n)),
      layers: ["fill", "outline", "symbol-fill", "symbol-line", "line", "selected", "draft-line", "box-fill", "box-line", "handles"].map((n) => this.id(n)),
    };
  }

  _attach({ keyboard, overlay }) {
    const m = this.map;
    for (const s of this.ownedIds.sources) m.addSource(s, { type: "geojson", data: EMPTY });
    const POLY = ["==", ["geometry-type"], "Polygon"];
    const LINE = ["==", ["geometry-type"], "LineString"];
    const SEL = ["==", ["get", "selected"], true];
    const paint = drawingPaint(this.drawingStyle);
    m.addLayer({ id: this.id("fill"), type: "fill", source: this.id("features"), filter: POLY,
      paint: { "fill-color": paint.fillColor, "fill-opacity": paint.fillOpacity } });
    m.addLayer({ id: this.id("outline"), type: "line", source: this.id("features"), filter: POLY,
      paint: { "line-color": paint.lineColor, "line-width": 1.5 } });
    // Over the outline, under lines and the selection; from zoom 19 (SYMBOLS above).
    const sym = symbolPaint(this.drawingStyle);
    m.addLayer({ id: this.id("symbol-fill"), type: "fill", source: this.id("symbols"), minzoom: SYMBOL_MIN_ZOOM,
      filter: ["==", ["get", "fill"], true], paint: { "fill-color": sym.fillColor, "fill-opacity": sym.fillOpacity } });
    m.addLayer({ id: this.id("symbol-line"), type: "line", source: this.id("symbols"), minzoom: SYMBOL_MIN_ZOOM,
      // MapLibre's default joins: round ones cost 2 ms a frame on the 57-master mix and 6 on hot fueling (SY3's bench).
      filter: ["==", ["geometry-type"], "LineString"], paint: { "line-color": sym.lineColor, "line-width": sym.lineWidth } });
    m.addLayer({ id: this.id("line"), type: "line", source: this.id("features"), filter: LINE,
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": lineColorPaint(this.drawingStyle), "line-width": 3 } });
    const selColor = selectColor(this.drawingStyle);
    m.addLayer({ id: this.id("selected"), type: "line", source: this.id("features"), filter: SEL,
      paint: { "line-color": selColor, "line-width": 2.5 } });
    m.addLayer({ id: this.id("draft-line"), type: "line", source: this.id("draft"),
      paint: { "line-color": selColor, "line-width": 2, "line-dasharray": [2, 1.5] } });
    m.addLayer({ id: this.id("box-fill"), type: "fill", source: this.id("box"), paint: { "fill-color": selColor, "fill-opacity": 0.08 } });
    m.addLayer({ id: this.id("box-line"), type: "line", source: this.id("box"), paint: { "line-color": selColor, "line-width": 1 } });
    m.addLayer({ id: this.id("handles"), type: "circle", source: this.id("handles"),
      paint: {
        // A segment's insert handle is smaller and hollow; the vertex Delete removes is filled.
        // The one under the pointer grows and fills (hover: _hoverAt).
        "circle-radius": ["case", ["==", ["get", "handle"], "rotate"], 6, ["get", "hover"], 7, ["==", ["get", "kind"], "mid"], 3.5, ["get", "active"], 6, 5],
        "circle-color": handleColorPaint(this.drawingStyle),
        "circle-stroke-color": "#0e7490", "circle-stroke-width": 1.5,
      } });

    this._handlers = [
      ["mousedown", (e) => this._onDown(e)],
      ["mousemove", (e) => this._onMove(e)],
      ["mouseup", (e) => this._onUp(e)],
      ["dblclick", (e) => this._onDblClick(e)],
      ["move", () => this._renderOverlay()],
    ];
    for (const [ev, fn] of this._handlers) m.on(ev, fn);

    // Shift-drag is box-select here, not MapLibre's box zoom.
    this._boxZoomWas = m.boxZoom?.isEnabled?.() ?? null;
    m.boxZoom?.disable?.();

    const container = m.getContainer?.();
    if (overlay && container) {
      const el = document.createElement("div");
      el.dataset.feInstance = this.instanceId;
      el.className = "fe-overlay";
      Object.assign(el.style, { position: "absolute", inset: "0", pointerEvents: "none", overflow: "hidden" });
      container.appendChild(el);
      this._overlay = el;
      this._labelsEl = document.createElement("div");
      this._labelsEl.className = "fe-labels";
      this._labelPool = new LabelPool(this._labelsEl, { make: makeFeatureLabel });
    }
    // Capture phase on the container: the key reaches the editor before
    // MapLibre's keyboard handler on the canvas container, so an arrow that
    // nudges the selection does not also pan the map. Keys only arrive while
    // focus is inside the container (the canvas is tabindex 0).
    if (keyboard && container) {
      this._keyTarget = container;
      this._onKeyBound = (e) => this._onKey(e);
      container.addEventListener("keydown", this._onKeyBound, true);
    }
  }

  destroy() {
    if (this._destroyed) return;
    this._destroyed = true;
    const m = this.map;
    // A React unmount can remove the map before the page's cleanup reaches
    // this: a removed map has no style to take ids out of.
    if (m._removed) {
      this._overlay?.remove();
      this._keyTarget?.removeEventListener("keydown", this._onKeyBound, true);
      return;
    }
    for (const [ev, fn] of this._handlers) m.off(ev, fn);
    const { sources, layers } = this.ownedIds;
    for (const l of [...layers].reverse()) if (m.getLayer(l)) m.removeLayer(l);
    for (const s of sources) if (m.getSource(s)) m.removeSource(s);
    if (this._boxZoomWas) m.boxZoom?.enable?.();
    m.dragPan?.enable?.();
    m.doubleClickZoom?.enable?.();
    this._overlay?.remove();
    this._keyTarget?.removeEventListener("keydown", this._onKeyBound, true);
  }

  // ── state ─────────────────────────────────────────────────────────────────

  _setFrameEpsg(epsg) {
    this.frame = isUtmEpsg(epsg) ? createFrame(epsg) : null;
  }

  /** Adopt the revision's local_epsg (the server's), recomputing every preview. */
  setFrame(epsg) {
    if (!isUtmEpsg(epsg)) throw new Error(`footprint-editor: ${epsg} is not a WGS 84 UTM zone`);
    this.localEpsg = epsg;
    if (this.frame?.epsg !== epsg) {
      this._cancelDrag();
      this._setFrameEpsg(epsg);
    }
    this.render();
  }

  setTemplates(list) {
    this.templates = new Map((list ?? []).map((t) => [t.id, t]));
    if (this.features) this.render();
  }

  setPerimeter(geometry) {
    this.perimeter = geometry;
    if (!this.localEpsg) this._setFrameEpsg(provisionalEpsg(geometry));
    this.render();
  }

  /** Replace the features with the server's (a GET), and forget history. */
  load(features, { localEpsg } = {}) {
    if (isUtmEpsg(localEpsg)) { this.localEpsg = localEpsg; this._setFrameEpsg(localEpsg); }
    // A line saved with a repeated point (before the line tool refused one)
    // loads without it; the next save stores it so.
    this.features = [...(features ?? [])].map((f) => withoutRepeats({ ...f }, this.frame));
    this.serverGeom = new Map();
    for (const f of this.features) {
      if (f.geom) this.serverGeom.set(f.id, { sigs: new Set([geometrySignature(f)]), geom: f.geom });
      delete f.geom;
    }
    this.history.clear();
    this._setSelection([]);
    this._emit("load");
  }

  /**
   * A save came back. Its geometries replace the previews of the features
   * still as they were sent; local_epsg is adopted first.
   * @param {{sent: object[], response: {features: object[], local_epsg?: number}}} p
   */
  applySaved({ sent = [], response }) {
    if (isUtmEpsg(response?.local_epsg)) this.setFrame(response.local_epsg);
    const sentById = new Map(sent.map((f) => [f.id, f]));
    this.serverGeom = new Map();
    for (const s of response?.features ?? []) {
      const sigs = new Set([geometrySignature(s)]);
      if (sentById.has(s.id)) sigs.add(geometrySignature(sentById.get(s.id)));
      this.serverGeom.set(s.id, { sigs, geom: s.geom });
    }
    this.render();
  }

  getFeatures() { return this.features; }
  getFeature(id) { return this.features.find((f) => f.id === id) ?? null; }
  toPayload() { return this.features.map((f) => payloadFeature(f, this.frame)); }
  template(f) { return f?.template_id ? this.templates.get(f.template_id) ?? null : null; }

  /** What the map draws for a feature: the server's geom while unchanged, else the preview. */
  displayGeometry(f) {
    const s = this.serverGeom.get(f.id);
    if (s && s.geom && s.sigs.has(geometrySignature(f))) return s.geom;
    return this.frame ? previewGeometry(f, this.template(f), this.frame) : null;
  }
  isPreview(f) {
    const s = this.serverGeom.get(f.id);
    return !(s && s.geom && s.sigs.has(geometrySignature(f)));
  }

  get canUndo() { return this.history.canUndo; }
  get canRedo() { return this.history.canRedo; }

  getSelection() { return [...this.selection]; }
  selectedFeatures() { return this.features.filter((f) => this.selection.has(f.id)); }

  select(ids, { add = false } = {}) {
    this._setSelection(add ? [...this.selection, ...ids] : ids);
  }
  clearSelection() { this._setSelection([]); }

  _setSelection(ids) {
    const known = new Set(this.features.map((f) => f.id));
    this.selection = new Set(ids.filter((id) => known.has(id)));
    // The selected vertex belongs to the one selected line, and still exists.
    const v = this.vertex, vf = v && this.getFeature(v.id);
    if (v && !(this.selection.size === 1 && this.selection.has(v.id) && v.index < (vf?.line_geom?.coordinates?.length ?? 0))) this.vertex = null;
    this.render();
    this.onSelectionChange?.(this.getSelection());
  }

  /** One operation: the new list, recorded for undo, drawn, announced. */
  _commit(label, next, { select } = {}) {
    if (this.readOnly) throw new Error(`footprint-editor: ${label} on a read-only instance`);
    const before = this.features;
    // No line keeps a zero-length segment, whatever made it (a vertex dragged onto its neighbor).
    next = next.map((f) => withoutRepeats(f, this.frame));
    this.features = next;
    this.history.push(label, before, next);
    if (select) this._setSelection(select);
    else this._setSelection([...this.selection]);
    this._emit(label);
  }

  _replace(updated, label) {
    const byId = new Map(updated.map((f) => [f.id, f]));
    this._commit(label, this.features.map((f) => byId.get(f.id) ?? f));
  }

  _emit(reason) {
    this.onChange?.({ reason, features: this.features, canUndo: this.canUndo, canRedo: this.canRedo });
  }

  undo() {
    if (this.readOnly) return false;
    const r = this.history.undo();
    if (!r) return false;
    this.features = r.features;
    this._setSelection([...this.selection]);
    this._emit(`undo:${r.label}`);
    return true;
  }

  redo() {
    if (this.readOnly) return false;
    const r = this.history.redo();
    if (!r) return false;
    this.features = r.features;
    this._setSelection([...this.selection]);
    this._emit(`redo:${r.label}`);
    return true;
  }

  setSnapping(patch) {
    this.snapping = { ...this.snapping, ...patch };
    return this.getSnapping();
  }
  getSnapping() { return { ...this.snapping }; }

  /** Align the snap frame to the pad: {mode: "long"}, {mode: "edge", ring, index}, or null (THE PAD AXIS). */
  setPadAxis(choice) {
    this.padChoice = choice && (choice.mode === "long" || choice.mode === "edge") ? { ...choice } : null;
    this._axisCache = null;
    this.render();
    return this.getPadAxis();
  }
  getPadChoice() { return this.padChoice ? { ...this.padChoice } : null; }
  /** The axis now ({origin, deg, source, ratio, nearSquare}), or null: off, or no perimeter, or an edge it no longer has. */
  getPadAxis() {
    const c = this._axisCache;
    if (c && c.perimeter === this.perimeter && c.frame === this.frame && c.choice === this.padChoice) return c.axis;
    const axis = padAxis(this.perimeter, this.frame, this.padChoice);
    this._axisCache = { perimeter: this.perimeter, frame: this.frame, choice: this.padChoice, axis };
    return axis;
  }
  /** The next click on a perimeter edge aligns to it (mode "edge"); Escape or cancel() stops. */
  startEdgePick() {
    if (!this.frame || !this.perimeter) return refuse("no_perimeter", "The plan needs a perimeter to align to.");
    this.cancel();
    this.mode = "edge";
    this.onModeChange?.(this.mode);
    return { ok: true };
  }

  /**
   * How features are inked: ink (a CSS color for footprints), outlines (no
   * footprint fill, a faint zone fill) and lineColors (a line's color by
   * layer), for Paper view's drawing look (padplanner/mapView.js), and paper
   * (symbols gate on Paper's scale, not the map's zoom). select, zone and
   * lineDefault recolor the selection (with the draft, the box and live
   * handles), zones and a line with no layer color, for a light sheet
   * (padplanner/paperTheme.js). {} is the default look.
   */
  setDrawingStyle({ ink = null, outlines = false, lineColors, paper = false, select = null, zone = null, lineDefault = null } = {}) {
    // lineColors absent keeps the palette the editor was built with.
    this.drawingStyle = {
      ink, outlines: Boolean(outlines), lineColors: lineColors === undefined ? this.drawingStyle.lineColors : lineColors, paper: paper === true,
      select, zone, lineDefault,
    };
    const p = drawingPaint(this.drawingStyle);
    if (this.map?.getLayer?.(this.id("fill"))) {
      this.map.setPaintProperty(this.id("fill"), "fill-color", p.fillColor);
      this.map.setPaintProperty(this.id("fill"), "fill-opacity", p.fillOpacity);
      this.map.setPaintProperty(this.id("outline"), "line-color", p.lineColor);
      this.map.setPaintProperty(this.id("line"), "line-color", lineColorPaint(this.drawingStyle));
      const sel = selectColor(this.drawingStyle);
      for (const [l, prop] of [["selected", "line-color"], ["draft-line", "line-color"], ["box-fill", "fill-color"], ["box-line", "line-color"]]) {
        this.map.setPaintProperty(this.id(l), prop, sel);
      }
      this.map.setPaintProperty(this.id("handles"), "circle-color", handleColorPaint(this.drawingStyle));
      const sym = symbolPaint(this.drawingStyle);
      this.map.setPaintProperty(this.id("symbol-line"), "line-color", sym.lineColor);
      this.map.setPaintProperty(this.id("symbol-fill"), "fill-color", sym.fillColor);
      const z = this.symbolMinZoom();
      for (const l of ["symbol-fill", "symbol-line"]) this.map.setLayerZoomRange?.(this.id(l), z, 24);
    }
    this.render();
    return { ...this.drawingStyle };
  }
  getDrawingStyle() { return { ...this.drawingStyle }; }

  /** Pause interaction, e.g. while MlDraw edits the perimeter on the same map. */
  setActive(active) {
    this._active = !!active;
    if (!active) { this._cancelDrag(); this.cancel(); }
  }

  /** Distinct layer values, in first-seen order. */
  layers() { return [...new Set(this.features.map((f) => f.layer).filter(Boolean))]; }
  setLayerVisibility(layer, visible) {
    if (visible) this.hiddenLayers.delete(layer); else this.hiddenLayers.add(layer);
    this._setSelection([...this.selection].filter((id) => !this.hiddenLayers.has(this.getFeature(id)?.layer)));
  }

  // ── operations ────────────────────────────────────────────────────────────

  _resizeStepFt() { return this.snapping.resize ? this.snapping.resizeStepFt : null; }

  _snapLngLat(lngLat) {
    if (!this.frame || !this.snapping.grid) return lngLat;
    return this.frame.toLngLat(snapPointToGrid(this.frame.toLocal(lngLat), this.snapping.gridFt, this.getPadAxis()));
  }

  /**
   * Place a template (or a freeform footprint or zone) at a point.
   * @param {object} spec  {template_id} or {kind, length_ft, width_ft | radius_ft}, and optionally layer, label
   * @param {number[]} lngLat
   */
  place(spec, lngLat) {
    if (this.readOnly) return READ_ONLY;
    if (!this.frame) return refuse("no_frame", "The plan needs a perimeter before anything is placed.");
    const t = spec.template_id ? this.templates.get(spec.template_id) : null;
    if (spec.template_id && !t) return refuse("template_unknown", "That template is not in this plan's palette.");
    const kind = t?.kind ?? spec.kind;
    if (!["footprint", "zone"].includes(kind)) return refuse("kind_invalid", "Place a footprint or a zone; lines are drawn.");
    if (!t && kind === "footprint" && !(spec.length_ft > 0 && spec.width_ft > 0)) return refuse("dims_required", "A freeform footprint needs a length and a width.");
    if (!t && kind === "zone" && !(spec.radius_ft > 0)) return refuse("radius_required", "A freeform zone needs a radius.");
    const f = {
      id: uuid4(), kind, template_id: t?.id ?? null,
      anchor: { type: "Point", coordinates: [...this._snapLngLat(lngLat)] },
      rotation_deg: 0, length_ft: t ? null : spec.length_ft ?? null, width_ft: t ? null : spec.width_ft ?? null,
      radius_ft: t ? null : spec.radius_ft ?? null, line_geom: null, well_api10: null,
      label: spec.label ?? null, layer: spec.layer ?? t?.default_layer ?? kind, props: {},
    };
    this._commit("place", [...this.features, f], { select: [f.id] });
    return { ok: true, feature: f };
  }

  /** Arm placement: the next click on the map places spec there. */
  startPlace(spec) {
    if (this.readOnly) return READ_ONLY;
    this.cancel();
    this.mode = "place";
    this._placeSpec = spec;
    this.onModeChange?.(this.mode);
  }

  startLine({ layer = "access_road", label = null, props = {} } = {}) {
    if (this.readOnly) return READ_ONLY;
    if (!LAYER_RE.test(layer ?? "")) return refuse("layer_invalid", "A line's type is its layer: lowercase letters, digits and underscores.");
    this.cancel();
    this.mode = "line";
    this._draft = { coords: [], cursor: null, layer, label, props: { ...props } };
    this.map.doubleClickZoom?.disable?.();
    this.onModeChange?.(this.mode);
  }

  /**
   * Add a vertex to the line being drawn; at most 500. A point within the
   * endpoint snap (lineEndPx) of the last one is not added: a double-click's
   * two presses, or a click and then a double-click on the same spot, place
   * one vertex.
   */
  addLinePoint(lngLat) {
    if (this.mode !== "line") return refuse("not_drawing", "No line is being drawn.");
    if (this._draft.coords.length >= LIMITS.linePoints[1]) return refuse("line_too_long", "A line has at most 500 points.");
    const at = this.snapVertex(lngLat);
    const last = this._draft.coords.at(-1);
    if (last && this.frame) {
      const a = this.frame.toLocal(last), b = this.frame.toLocal(at.lngLat);
      if (Math.hypot(b[0] - a[0], b[1] - a[1]) <= this.snapping.lineEndPx * this._mPerPx()) {
        return refuse("point_repeated", "That point is on the last one.");
      }
    }
    this._draft.coords.push(at.lngLat);
    this._renderDraft();
    return { ok: true, points: this._draft.coords.length, snapped: at.snapped };
  }

  finishLine() {
    if (this.mode !== "line") return refuse("not_drawing", "No line is being drawn.");
    const d = this._draft;
    if (d.coords.length < LIMITS.linePoints[0]) return refuse("line_too_short", "A line has at least two points.");
    const f = {
      id: uuid4(), kind: "line", template_id: null, anchor: null, rotation_deg: 0, length_ft: null, width_ft: null,
      radius_ft: null, line_geom: { type: "LineString", coordinates: d.coords }, well_api10: null,
      label: d.label, layer: d.layer, props: d.props,
    };
    this.cancel();
    this._commit("line", [...this.features, f], { select: [f.id] });
    return { ok: true, feature: f };
  }

  /** The line being drawn: its vertices and its lengths so far (with the cursor's segment), or null. */
  draftLine() {
    const d = this._draft;
    if (!d || !this.frame) return null;
    const coords = [...d.coords, ...(d.cursor ? [d.cursor] : [])];
    return { coords, points: d.coords.length, layer: d.layer, ...lineLengthsFt(coords, this.frame) };
  }

  /**
   * Where a line vertex at lngLat would land: snapped to another line's
   * endpoint, then a perimeter edge, then the grid (LINES above). `exclude`
   * is the line being edited, whose own vertices are not targets.
   * @returns {{lngLat: number[], snapped: null|"endpoint"|"perimeter"|"grid"}}
   */
  snapVertex(lngLat, { exclude = null } = {}) {
    if (!this.frame) return { lngLat: [...lngLat], snapped: null };
    const mpp = this._mPerPx();
    const endpoints = [];
    if (this.snapping.lineEnds) {
      for (const f of this._current()) {
        if (f.kind !== "line" || f.id === exclude || this.hiddenLayers.has(f.layer)) continue;
        const c = f.line_geom?.coordinates ?? [];
        if (c.length) endpoints.push(this.frame.toLocal(c[0]), this.frame.toLocal(c[c.length - 1]));
      }
    }
    const r = snapLinePoint(this.frame.toLocal(lngLat), {
      grid: this.snapping.grid, gridFt: this.snapping.gridFt, axis: this.getPadAxis(),
      edgesLocal: this.snapping.perimeter ? perimeterLocal(this.perimeter, this.frame) : [], tolM: this.snapping.perimeterPx * mpp,
      endpoints, endpointTolM: this.snapping.lineEndPx * mpp,
    });
    return { lngLat: r.snapped ? this.frame.toLngLat(r.en) : [...lngLat], snapped: r.snapped };
  }

  // ── line vertices ─────────────────────────────────────────────────────────

  _lineOp(id, label, fn) {
    if (this.readOnly) return READ_ONLY;
    const f = this.getFeature(id);
    if (!f || f.kind !== "line") return refuse("not_a_line", "That is not a line.");
    const next = fn(f);
    if (!next) return null;
    this._replace([next], label);
    return { ok: true, feature: next };
  }

  /** Move vertex i of line id to lngLat (snapped unless snap is false). One operation. */
  moveLineVertex(id, i, lngLat, { snap = true } = {}) {
    const at = snap ? this.snapVertex(lngLat, { exclude: id }).lngLat : lngLat;
    return this._lineOp(id, "vertex", (f) => moveVertex(f, i, at)) ?? refuse("vertex_unknown", "No such vertex.");
  }

  /** Insert a vertex at lngLat after vertex i (snapped unless snap is false). One operation. */
  insertLineVertex(id, i, lngLat, { snap = true } = {}) {
    const at = snap ? this.snapVertex(lngLat, { exclude: id }).lngLat : lngLat;
    const r = this._lineOp(id, "insert-vertex", (f) => insertVertex(f, i, at));
    if (r?.ok) this.vertex = { id, index: i + 1 };
    return r ?? refuse("vertex_unknown", "No segment there, or the line has 500 points.");
  }

  /** Remove vertex i of line id; a line keeps two. One operation. */
  deleteLineVertex(id, i) {
    const f = this.getFeature(id);
    if (f?.kind === "line" && (f.line_geom?.coordinates?.length ?? 0) <= LIMITS.linePoints[0]) {
      return refuse("line_too_short", "A line keeps at least two points; delete the line instead.");
    }
    const r = this._lineOp(id, "delete-vertex", (g) => deleteVertex(g, i));
    if (r?.ok) this.vertex = null;
    return r ?? refuse("vertex_unknown", "No such vertex.");
  }

  /** The vertex Delete removes: {id, index}, or null. */
  getSelectedVertex() { return this.vertex ? { ...this.vertex } : null; }
  selectVertex(id, index) {
    const f = this.getFeature(id);
    this.vertex = f?.kind === "line" && index >= 0 && index < (f.line_geom?.coordinates?.length ?? 0) ? { id, index } : null;
    // _setSelection keeps a vertex of the one selected line, and announces.
    if (this.vertex && !(this.selection.size === 1 && this.selection.has(id))) {
      const v = this.vertex;
      this._setSelection([id]);
      this.vertex = v;
      this.render();
    } else {
      this.render();
      this.onSelectionChange?.(this.getSelection());
    }
    return this.getSelectedVertex();
  }

  /** Leave place or line mode without a change. */
  cancel() {
    const was = this.mode;
    this.mode = "select";
    this._placeSpec = null;
    this._draft = null;
    this.map.doubleClickZoom?.enable?.();
    this._renderDraft();
    if (was !== "select") this.onModeChange?.(this.mode);
  }

  /** Move the selection (or ids) by [dE, dN] meters in the frame, unsnapped. */
  moveBy(delta, ids = this.getSelection()) {
    if (this.readOnly) return READ_ONLY;
    if (!this.frame) return refuse("no_frame", "No frame.");
    const set = new Set(ids);
    this._replace(this.features.filter((f) => set.has(f.id)).map((f) => translate(f, this.frame, delta)), "move");
    return { ok: true };
  }

  /**
   * Numeric entry for one feature. position is {e, n} (frame meters) or
   * {lng, lat}; the other fields are the stored columns. Refused values
   * change nothing.
   */
  setFields(id, fields) {
    if (this.readOnly) return READ_ONLY;
    const f = this.getFeature(id);
    if (!f) return refuse("feature_unknown", "No such feature.");
    let next = { ...f };
    if (fields.position) {
      const p = fields.position;
      const en = Number.isFinite(p.e) && Number.isFinite(p.n) ? [p.e, p.n]
        : Number.isFinite(p.lng) && Number.isFinite(p.lat) && this.frame ? this.frame.toLocal([p.lng, p.lat]) : null;
      if (!en || !this.frame) return refuse("position_invalid", "Position is UTM E/N in meters or lat/lng.");
      const ref = referenceEN(f, this.frame);
      next = translate(next, this.frame, [en[0] - ref[0], en[1] - ref[1]]);
    }
    if ("rotation_deg" in fields) {
      if (f.kind !== "footprint") return refuse("rotation_not_applicable", "Only a footprint rotates.");
      if (!Number.isFinite(fields.rotation_deg)) return refuse("rotation_invalid", "Rotation is a number of degrees.");
      next.rotation_deg = normalizeDeg(fields.rotation_deg);
    }
    for (const [k, lim, kind] of [["length_ft", LIMITS.lengthFt, "footprint"], ["width_ft", LIMITS.widthFt, "footprint"], ["radius_ft", LIMITS.radiusFt, "zone"]]) {
      if (!(k in fields)) continue;
      if (f.kind !== kind) return refuse(`${k}_not_applicable`, `${k} applies to a ${kind}.`);
      const v = fields[k];
      if (v === null && f.template_id) { next[k] = null; continue; }
      if (!(typeof v === "number" && v >= lim[0] && v <= lim[1])) {
        return refuse(`${k}_out_of_range`, `${k} is ${lim[0].toLocaleString("en-US")} to ${lim[1].toLocaleString("en-US")} feet.`);
      }
      next[k] = v;
    }
    if ("label" in fields) {
      const l = fields.label === "" ? null : fields.label;
      if (l !== null && !(typeof l === "string" && l.length <= 120)) return refuse("label_invalid", "A label is at most 120 characters.");
      next.label = l;
    }
    if ("layer" in fields) {
      if (!LAYER_RE.test(fields.layer ?? "")) return refuse("layer_invalid", "A layer is lowercase letters, digits and underscores, starting with a letter or digit, at most 40.");
      next.layer = fields.layer;
    }
    // A line's disturbance width: props.width_ft (151 keeps width_ft for
    // footprints), entered only, never defaulted; null removes it.
    if ("line_width_ft" in fields) {
      if (f.kind !== "line") return refuse("line_width_not_applicable", "A disturbance width applies to a line.");
      const v = fields.line_width_ft;
      const { width_ft: _was, ...rest } = f.props ?? {};
      if (v === null) next.props = rest;
      else if (typeof v === "number" && v >= LIMITS.widthFt[0] && v <= LIMITS.widthFt[1]) next.props = { ...rest, width_ft: v };
      else return refuse("line_width_out_of_range", `A width is ${LIMITS.widthFt[0]} to ${LIMITS.widthFt[1].toLocaleString("en-US")} feet.`);
    }
    if ("well_api10" in fields) {
      const w = fields.well_api10;
      if (w !== null) {
        if (!["footprint", "zone"].includes(f.kind)) return refuse("well_link_kind", "Only a footprint or a zone can mark a well.");
        const other = this.features.find((g) => g.id !== id && g.well_api10 === w);
        if (other) return refuse("well_already_linked", "Another feature already marks that well.");
      }
      next.well_api10 = w;
    }
    this._replace([next], "edit");
    return { ok: true, feature: next };
  }

  duplicate(ids = this.getSelection()) {
    if (this.readOnly) return READ_ONLY;
    if (!this.frame) return refuse("no_frame", "No frame.");
    const set = new Set(ids);
    const d = ftToM(DUPLICATE_OFFSET_FT);
    // One feature per well: a copy marks no well.
    const copies = this.features.filter((f) => set.has(f.id))
      .map((f) => ({ ...translate(f, this.frame, [d, -d]), id: uuid4(), well_api10: null }));
    if (!copies.length) return refuse("nothing_selected", "Select something to duplicate.");
    this._commit("duplicate", [...this.features, ...copies], { select: copies.map((c) => c.id) });
    return { ok: true, features: copies };
  }

  remove(ids = this.getSelection()) {
    if (this.readOnly) return READ_ONLY;
    const set = new Set(ids);
    if (!this.features.some((f) => set.has(f.id))) return refuse("nothing_selected", "Select something to delete.");
    this._commit("delete", this.features.filter((f) => !set.has(f.id)), { select: [] });
    return { ok: true };
  }

  alignSelection(mode) {
    if (this.readOnly) return READ_ONLY;
    const sel = this.selectedFeatures();
    if (sel.length < 2) return refuse("align_needs_two", "Select two or more to align.");
    this._replace(align(sel, this.templates, this.frame, mode), `align:${mode}`);
    return { ok: true };
  }

  // ── pointer ───────────────────────────────────────────────────────────────

  _hit(point, layers) {
    const hits = this.map.queryRenderedFeatures?.(point, { layers }) ?? [];
    return hits.find((h) => h?.properties) ?? null;
  }

  _cursorEN(e) { return this.frame.toLocal([e.lngLat.lng, e.lngLat.lat]); }

  /** The handle under a screen point: MapLibre's hit, else the nearest drawn within HANDLE_SLOP_PX. */
  _handleAt(point) {
    const h = this._hit(point, [this.id("handles")]);
    return h ? h.properties.handle : this._nearestHandle(point, this._handles);
  }

  _nearestHandle(point, handles) {
    let best = null, bestD = HANDLE_SLOP_PX;
    for (const f of handles ?? []) {
      const p = this.map.project(f.geometry.coordinates);
      const d = Math.hypot(p.x - point.x, p.y - point.y);
      if (d <= bestD) { best = f.properties.handle; bestD = d; }
    }
    return best;
  }

  /** The handle under the pointer, of these (null while drawing, placing, or dragging). */
  _hoverOf(handles) {
    const p = this._pointer;
    return p && !this._drag && this.mode === "select" ? this._nearestHandle(p, handles) : null;
  }

  /** The handle the pointer is over grows and fills, and the cursor says it can be taken. */
  _hoverAt(point) {
    this._pointer = point;
    if (this._hoverOf(this._handles) !== (this._hover ?? null)) this._renderHandles();
  }
  _featureLayers() { return [this.id("fill"), this.id("outline"), this.id("line")]; }

  _onDown(e) {
    if (!this._active || this._destroyed || !this.frame) return;
    if ((e.originalEvent?.button ?? 0) !== 0) return;
    if (this.readOnly) {
      // A click picks what is under it, or clears; the map still pans.
      const hit = this._hit(e.point, this._featureLayers());
      this._setSelection(hit ? [hit.properties.id] : []);
      return;
    }
    const shift = !!e.originalEvent?.shiftKey;
    const at = [e.lngLat.lng, e.lngLat.lat];
    if (this.mode === "place") {
      const spec = this._placeSpec;
      this.cancel();
      this.place(spec, at);
      e.preventDefault?.();
      return;
    }
    if (this.mode === "line") { this.addLinePoint(at); e.preventDefault?.(); return; }
    if (this.mode === "edge") {
      // A miss keeps picking; the page's hint says to click an edge.
      const hit = edgeNear(this.perimeter, this.frame, this.frame.toLocal(at), this.snapping.perimeterPx * this._mPerPx());
      e.preventDefault?.();
      if (!hit) return;
      this.cancel();
      this.setPadAxis({ mode: "edge", ring: hit.ring, index: hit.index });
      this.onPadAxisChange?.(this.getPadChoice());
      return;
    }

    const handle = this.selection.size === 1 ? this._handleAt(e.point) : null;
    if (handle) {
      const f = this.getFeature([...this.selection][0]);
      const lh = f?.kind === "line" ? lineHandle(handle) : null;
      if (lh?.type === "vertex" && e.originalEvent?.altKey) {
        this.deleteLineVertex(f.id, lh.index);
        e.preventDefault?.();
        return;
      }
      this._startDrag({ type: "handle", handle, start: f, line: lh, startPoint: e.point }, e);
      return;
    }
    const hit = this._hit(e.point, this._featureLayers());
    if (hit) {
      const id = hit.properties.id;
      if (shift) {
        const s = new Set(this.selection);
        if (s.has(id)) s.delete(id); else s.add(id);
        this._setSelection([...s]);
        if (!s.has(id)) return;
      } else if (!this.selection.has(id)) {
        this._setSelection([id]);
      }
      // A press on one feature of a multi-selection keeps the selection for a
      // drag (they move together), and narrows it to that feature on a click.
      const collapseTo = !shift && this.selection.size > 1 ? id : null;
      this._startDrag({ type: "move", startEN: this._cursorEN(e), start: this.selectedFeatures(), collapseTo }, e);
      return;
    }
    if (shift) { this._startDrag({ type: "box", startPoint: e.point, add: true }, e); return; }
    this._drag = { type: "maybe-clear", startPoint: e.point };
  }

  _startDrag(d, e) {
    this._drag = d;
    this.map.dragPan?.disable?.();
    e.preventDefault?.();
  }

  _cancelDrag() {
    this._drag = null;
    if (this._preview) this.onPreview?.(null);
    this._preview = null;
    this.map.dragPan?.enable?.();
    this.map.getSource(this.id("box"))?.setData(EMPTY);
  }

  _onMove(e) {
    if (this._destroyed || !this.frame) return;
    this._pointer = e.point;
    if (this.mode === "line" && this._draft) {
      // The cursor's segment shows where the next vertex would land.
      this._draft.cursor = this.snapVertex([e.lngLat.lng, e.lngLat.lat]).lngLat;
      this._renderDraft();
      this._renderOverlay();
      return;
    }
    const d = this._drag;
    if (!d) { this._hoverAt(e.point); return; }
    if (d.type === "maybe-clear") return;
    if (d.type === "box") {
      d.endPoint = e.point;
      const a = this.map.unproject([d.startPoint.x, d.startPoint.y]), b = this.map.unproject([e.point.x, e.point.y]);
      const ring = [[a.lng, a.lat], [b.lng, a.lat], [b.lng, b.lat], [a.lng, b.lat], [a.lng, a.lat]];
      this.map.getSource(this.id("box"))?.setData({ type: "FeatureCollection", features: [{ type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [ring] } }] });
      return;
    }
    const cur = this._cursorEN(e);
    const moved = this.dragTo(cur);
    this._preview = new Map(moved.map((f) => [f.id, f]));
    this.render();
    this.onPreview?.(moved);
  }

  /**
   * What the current drag would make of the features at cursorEN (frame
   * meters), snapped. Exposed so the handle math is testable without pointer
   * events.
   */
  dragTo(cursorEN) {
    const d = this._drag;
    if (!d) return [];
    if (d.type === "move") {
      const delta = [cursorEN[0] - d.startEN[0], cursorEN[1] - d.startEN[1]];
      const tolM = this.snapping.perimeter ? this.snapping.perimeterPx * this._mPerPx() : 0;
      const r = snapMove({ features: d.start, templates: this.templates, frame: this.frame, delta, snapping: { ...this.snapping, axis: this.getPadAxis() },
        edgesLocal: perimeterLocal(this.perimeter, this.frame), tolM });
      return d.start.map((f) => translate(f, this.frame, r.delta));
    }
    if (d.type === "handle") {
      const f = d.start;
      if (d.line) {
        const at = this.snapVertex(this.frame.toLngLat(cursorEN), { exclude: f.id }).lngLat;
        const next = d.line.type === "vertex" ? moveVertex(f, d.line.index, at) : insertVertex(f, d.line.index, at);
        return next ? [next] : [];
      }
      if (d.handle === "rotate") {
        return [{ ...f, rotation_deg: rotationToward(f, this.frame, cursorEN, { stepDeg: this.snapping.rotation ? this.snapping.rotationStepDeg : null, offsetDeg: this.getPadAxis()?.deg ?? 0 }) }];
      }
      if (d.handle === "radius") return [resizeZone(f, this.frame, cursorEN, { stepFt: this._resizeStepFt() })];
      return [resizeFootprint(f, this.template(f), this.frame, d.handle, cursorEN, { stepFt: this._resizeStepFt() })];
    }
    return [];
  }

  /** Begin a handle drag programmatically (tests, and the smoke harness). */
  beginHandleDrag(id, handle) {
    if (this.readOnly) return false;
    const f = this.getFeature(id);
    if (!f) return false;
    this._drag = { type: "handle", handle, start: f, line: f.kind === "line" ? lineHandle(handle) : null };
    return true;
  }
  /** Begin a move of the selection from startEN. */
  beginMove(startEN) {
    if (this.readOnly) return;
    this._drag = { type: "move", startEN, start: this.selectedFeatures() };
  }
  /** End the drag at cursorEN: one operation. */
  endDrag(cursorEN) {
    const d = this._drag;
    if (!d) return;
    const moved = cursorEN ? this.dragTo(cursorEN) : [...(this._preview?.values() ?? [])];
    const label = d.type === "move" ? "move" : d.line ? (d.line.type === "vertex" ? "vertex" : "insert-vertex")
      : d.handle === "rotate" ? "rotate" : "resize";
    this._cancelDrag();
    if (moved.length) {
      this._replace(moved, label);
      if (d.line) this.vertex = { id: d.start.id, index: d.line.type === "vertex" ? d.line.index : d.line.index + 1 };
      this.render();
    } else this.render();
  }

  _onUp(e) {
    const d = this._drag;
    if (!d) return;
    if (d.type === "maybe-clear") {
      const moved = Math.hypot(e.point.x - d.startPoint.x, e.point.y - d.startPoint.y) > 3;
      this._drag = null;
      if (!moved) this.clearSelection();
      return;
    }
    if (d.type === "box") {
      const end = d.endPoint ?? e.point;
      this._cancelDrag();
      this.select(this._inScreenBox(d.startPoint, end), { add: d.add });
      return;
    }
    if (!this._preview) {
      this._cancelDrag();
      if (d.collapseTo) this._setSelection([d.collapseTo]);
      // A click (no drag) on a line's handle: a vertex is picked for Delete;
      // a midpoint inserts a vertex there.
      if (d.line?.type === "vertex") this.selectVertex(d.start.id, d.line.index);
      else if (d.line?.type === "mid") {
        const c = d.start.line_geom.coordinates, a = this.frame.toLocal(c[d.line.index]), b = this.frame.toLocal(c[d.line.index + 1]);
        this.insertLineVertex(d.start.id, d.line.index, this.frame.toLngLat([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]), { snap: false });
      }
      return;
    }
    this.endDrag(null);
  }

  /** Visible features with any vertex inside the screen box, or the box inside them. */
  _inScreenBox(p0, p1) {
    const x0 = Math.min(p0.x, p1.x), x1 = Math.max(p0.x, p1.x), y0 = Math.min(p0.y, p1.y), y1 = Math.max(p0.y, p1.y);
    const out = [];
    for (const f of this.features) {
      if (this.hiddenLayers.has(f.layer)) continue;
      const rings = localRings(f, this.template(f), this.frame) ?? [];
      let bx0 = Infinity, bx1 = -Infinity, by0 = Infinity, by1 = -Infinity;
      for (const r of rings) for (const en of r) {
        const p = this.map.project(this.frame.toLngLat(en));
        bx0 = Math.min(bx0, p.x); bx1 = Math.max(bx1, p.x); by0 = Math.min(by0, p.y); by1 = Math.max(by1, p.y);
      }
      if (bx1 >= x0 && bx0 <= x1 && by1 >= y0 && by0 <= y1) out.push(f.id);
    }
    return out;
  }

  _onDblClick(e) {
    if (this.mode !== "line") return;
    e.preventDefault?.();
    // Its second press placed nothing (addLinePoint refuses a repeat).
    this.finishLine();
  }

  /**
   * Delete/Backspace, Ctrl/Cmd+Z, Ctrl/Cmd+Shift+Z (and Ctrl+Y), Ctrl/Cmd+D,
   * arrows (nudge by the grid step, whether or not the grid is on), Enter
   * (finish a line), Escape. Nothing fires while an input has focus.
   */
  _onKey(e) {
    if (!this._active || this._destroyed || isTextEntry(e.target)) return;
    const mod = e.ctrlKey || e.metaKey;
    const k = e.key.toLowerCase();
    const nudge = { arrowleft: [-1, 0], arrowright: [1, 0], arrowup: [0, 1], arrowdown: [0, -1] }[k];
    let done = true;
    if (nudge && !mod) {
      if (!this.selection.size || !this.frame) return;
      const g = ftToM(this.snapping.gridFt);
      this.moveBy([nudge[0] * g, nudge[1] * g]);
    } else if (k === "delete" || k === "backspace") {
      if (this.vertex) this.deleteLineVertex(this.vertex.id, this.vertex.index);
      else if (!this.selection.size) return;
      else this.remove();
    } else if (mod && k === "z" && !e.shiftKey) this.undo();
    else if (mod && ((k === "z" && e.shiftKey) || k === "y")) this.redo();
    else if (mod && k === "d") this.duplicate();
    else if (k === "enter" && this.mode === "line") this.finishLine();
    else if (k === "escape") {
      if (this.mode !== "select") this.cancel();
      else if (this.vertex) { this.vertex = null; this.render(); }
      else this.clearSelection();
    }
    else done = false;
    if (done) { e.preventDefault(); e.stopPropagation(); }
  }

  // ── drawing ───────────────────────────────────────────────────────────────

  _mPerPx() {
    const c = this.map.getCenter?.();
    const z = this.map.getZoom?.();
    return c && Number.isFinite(z) ? metersPerPixel(c.lat, z) : 0;
  }

  /** The scale bar, in feet: {feet, px, label}. */
  scaleBar(maxPx = 100) {
    const mpp = this._mPerPx();
    return mpp ? scaleBarFeet(mpp, maxPx) : null;
  }

  _current() {
    if (!this._preview) return this.features;
    return this.features.map((f) => this._preview.get(f.id) ?? f);
  }

  /**
   * The zoom symbols draw from: 19 on the map; in Paper view the zoom at which
   * a CSS inch is 40 ft at the plan's latitude (its perimeter's centroid, else
   * the map's center), symbols.js paperSymbolMinZoom.
   */
  symbolMinZoom() {
    if (!this.drawingStyle.paper) return SYMBOL_MIN_ZOOM;
    const lat = perimeterCentroid(this.perimeter)?.[1] ?? this.map.getCenter?.()?.lat;
    return Number.isFinite(lat) ? paperSymbolMinZoom(lat) : SYMBOL_MIN_ZOOM;
  }

  /** One feature's symbol in 4326, kept until it, its template or the frame changes. */
  _symbolOf(f) {
    const template = this.template(f);
    if (f.kind !== "footprint" || !template?.symbol) { this._symbols.delete(f.id); return null; }
    const sig = geometrySignature(f);
    const hit = this._symbols.get(f.id);
    if (hit && hit.sig === sig && hit.template === template && hit.frame === this.frame) return hit.feats;
    const feats = symbolFeatures(f, template, this.frame);
    this._symbols.set(f.id, { sig, template, frame: this.frame, feats });
    return feats;
  }

  render() {
    if (this._destroyed || !this.features) return;
    const m = this.map;
    const list = this._current().filter((f) => !this.hiddenLayers.has(f.layer));
    const feats = [];
    const symbols = [];
    const labels = [];
    for (const f of list) {
      if (this.frame) { const s = this._symbolOf(f); if (s) for (const x of s) symbols.push(x); }
      const geometry = this._preview?.has(f.id)
        ? previewGeometry(f, this.template(f), this.frame)
        : this.displayGeometry(f);
      if (!geometry) continue;
      const color = f.kind === "line" ? this.drawingStyle.lineColors?.[f.layer] ?? null : null;
      feats.push({ type: "Feature", properties: { id: f.id, kind: f.kind, layer: f.layer, selected: this.selection.has(f.id), ...(color ? { color } : {}) }, geometry });
      const named = typeof f.label === "string" ? f.label.trim() : "";
      // A line is labeled with its length, at its midpoint, in every view.
      const text = f.kind === "line" && this.frame
        ? [named, formatFeet(lineLengthsFt(f.line_geom?.coordinates, this.frame).total)].filter(Boolean).join(" · ")
        : named;
      if (text) {
        const at = labelPoint(geometry);
        if (at) labels.push({ id: f.id, text, lngLat: at, selected: this.selection.has(f.id), ...(f.kind === "line" ? { ends: labelSegment(geometry) } : {}) });
      }
    }
    // Selected first, so the cell cull never drops the label being edited.
    this._labels = [...labels.filter((l) => l.selected), ...labels.filter((l) => !l.selected)];
    m.getSource(this.id("features"))?.setData({ type: "FeatureCollection", features: feats });
    m.getSource(this.id("symbols"))?.setData({ type: "FeatureCollection", features: symbols });
    if (this._symbols.size > list.length) {
      const live = new Set(list.map((f) => f.id));
      for (const id of this._symbols.keys()) if (!live.has(id)) this._symbols.delete(id);
    }
    this._renderHandles(list);
    this._renderOverlay();
  }

  /** The selected feature's handles (one feature selected, editable); kept for _handleAt. */
  _renderHandles(list = this._current().filter((f) => !this.hiddenLayers.has(f.layer))) {
    const handles = [];
    if (this.selection.size === 1 && this.frame && !this.readOnly) {
      const f = list.find((g) => this.selection.has(g.id));
      if (f) for (const h of handlesFor(f, this.template(f), this.frame)) {
        const lh = f.kind === "line" ? lineHandle(h.handle) : null;
        const active = lh?.type === "vertex" && this.vertex?.id === f.id && this.vertex.index === lh.index;
        handles.push({ type: "Feature", properties: { handle: h.handle, id: f.id, kind: lh?.type ?? "shape", active, hover: false }, geometry: { type: "Point", coordinates: h.lngLat } });
      }
    }
    // Hover is where the pointer is now, among these handles: an insert renumbers them.
    const hover = this._hoverOf(handles);
    for (const h of handles) h.properties.hover = h.properties.handle === hover;
    if (hover !== (this._hover ?? null)) {
      const canvas = this.map.getCanvas?.();
      if (canvas) canvas.style.cursor = hover ? "pointer" : "";
    }
    this._hover = hover;
    this._handles = handles;
    this.map.getSource(this.id("handles"))?.setData({ type: "FeatureCollection", features: handles });
  }

  _renderDraft() {
    const d = this._draft;
    const coords = d ? [...d.coords, ...(d.cursor ? [d.cursor] : [])] : [];
    this.map.getSource(this.id("draft"))?.setData(coords.length > 1
      ? { type: "FeatureCollection", features: [{ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: coords } }] }
      : EMPTY);
  }

  /** The labels the overlay draws: edge lengths of the selection (at most 10 features) and of the line being drawn. */
  dimensionLabels() {
    if (!this.frame) return [];
    const draft = this.draftLine();
    return [
      ...this._current().filter((f) => this.selection.has(f.id) && !this.hiddenLayers.has(f.layer)).slice(0, 10)
        .flatMap((f) => dimensionLabels(f, this.template(f), this.frame)),
      // The line being drawn: each segment, the cursor's included, and the total.
      ...(draft ? lineDimensionLabels(draft.coords, this.frame) : []),
    ];
  }

  /** The feature labels the overlay draws now: [{id, text, x, y}], after the cull and the cap. */
  visibleLabels() {
    const c = this.map.getContainer?.();
    return cullLabels(this._labels ?? [], {
      project: (ll) => this.map.project(ll), width: c?.clientWidth ?? 0, height: c?.clientHeight ?? 0,
      cell: LABEL_CELL, max: MAX_LABELS, margin: LABEL_MARGIN,
    }).map(({ item, x, y }) => ({ id: item.id, text: item.text, x, y, ...(item.ends ? { ends: item.ends } : {}) }));
  }

  _renderLabels() {
    if (!this._labelsEl) return;
    this._labelPool.render(this.visibleLabels(), (s, l) => {
      if (s.textContent !== l.text) s.textContent = l.text;
      s.dataset.featureId = l.id;
      // A line's sits beside it, below on screen (its lengths go above), clear
      // of the midpoint handle under it.
      const u = l.ends ? this._beside(l.ends, "below") : null;
      s.style.transform = u
        ? `translate(${l.x + u.x * LINE_LABEL_GAP}px, ${l.y + u.y * LINE_LABEL_GAP}px) translate(${-50 + 50 * u.x}%, ${-50 + 50 * u.y}%)`
        : `translate(${l.x}px, ${l.y}px) translate(-50%, -50%)`;
    });
  }

  /** The screen unit normal of a segment (lng/lat ends), on the side named: "above" or "below" on screen. */
  _beside([a, b], side) {
    const p = this.map.project(a), q = this.map.project(b);
    const len = Math.hypot(q.x - p.x, q.y - p.y);
    if (!(len > 1e-6)) return null;
    let x = (q.y - p.y) / len, y = (p.x - q.x) / len;
    // Upward: y < 0 (a vertical segment's, rightward).
    if (y > 0 || (y === 0 && x < 0)) { x = -x; y = -y; }
    return side === "above" ? { x, y } : { x: -x, y: -y };
  }

  _renderOverlay() {
    const el = this._overlay;
    if (!el || this._destroyed) return;
    el.replaceChildren(this._labelsEl);
    this._renderLabels();
    for (const l of this.dimensionLabels()) {
      const p = this.map.project(l.lngLat);
      // Outward from the shape's middle, so the label sits off the edge; a
      // line segment's beside it (screen-upward side), clear of its midpoint handle.
      let ux = 0, uy = 0;
      if (l.ends) {
        const u = this._beside(l.ends, "above");
        if (u) { ux = u.x; uy = u.y; }
      } else if (l.center) {
        const c = this.map.project(l.center);
        const len = Math.hypot(p.x - c.x, p.y - c.y);
        if (len > 1e-6) { ux = (p.x - c.x) / len; uy = (p.y - c.y) / len; }
      }
      const gap = l.ends || l.total ? LINE_LABEL_GAP : 6;
      const s = document.createElement("span");
      s.className = "fe-dim";
      s.textContent = l.text;
      Object.assign(s.style, {
        position: "absolute", left: `${p.x + ux * gap}px`, top: `${p.y + uy * gap}px`,
        transform: `translate(${-50 + 50 * ux}%, ${-50 + 50 * uy}%)`,
        font: "11px/1.2 ui-monospace, SFMono-Regular, Menlo, monospace", color: "#f8fafc",
        background: "rgba(15, 23, 42, 0.75)", padding: "1px 4px", borderRadius: "3px", whiteSpace: "nowrap",
      });
      el.appendChild(s);
    }
    const sb = this.scaleBarAt ? this.scaleBar() : null;
    if (sb) {
      const at = this.scaleBarAt;
      const bar = document.createElement("div");
      bar.className = "fe-scale";
      bar.dataset.feet = String(sb.feet);
      Object.assign(bar.style, {
        position: "absolute", ...(at.right !== undefined ? { right: `${at.right}px` } : { left: `${at.left ?? 12}px` }),
        bottom: `${at.bottom ?? 12}px`, width: `${sb.px}px`, borderBottom: "2px solid #f8fafc",
        borderLeft: "2px solid #f8fafc", borderRight: "2px solid #f8fafc", height: "6px",
      });
      const t = document.createElement("span");
      t.textContent = sb.label;
      Object.assign(t.style, { position: "absolute", bottom: "8px", left: "0", font: "11px/1 ui-sans-serif, system-ui", color: "#f8fafc", whiteSpace: "nowrap" });
      bar.appendChild(t);
      el.appendChild(bar);
    }
  }
}
